import { useCallback } from 'react';
import { AiSummary } from '@shared/scenes/ask/components/AiSummary';
import { FieldRecord } from '@shared/scenes/entity/types';
import { API } from '@shared/types/api';
import { buildSubformSummaryPrompt } from './buildSubformSummaryPrompt';

interface Props {
  /** The card (workflow-entity) id — the Ask scope the summary is resolved against. */
  entityId: string;
  workflowTemplate: API.WorkflowTemplateFragment | undefined;
  orderedComponents: API.AnyComponent[];
  fields: FieldRecord;
  /**
   * The already-generated summary, if any. Held in card-scoped state above the
   * subform view so it survives closing and reopening the subform while on the
   * card, and is lost only on refresh or when leaving the card.
   */
  summary: string | undefined;
  /** Stores the generated summary in the card-scoped state. */
  onSummaryGenerated: (summary: string) => void;
}

/**
 * "Summarize" widget for a single subform, scoped to the subform's own field data.
 * See {@link buildSubformSummaryPrompt} for how the subform is isolated in the prompt.
 */
export function SubformSummary({
  entityId,
  workflowTemplate,
  orderedComponents,
  fields,
  summary,
  onSummaryGenerated,
}: Props) {
  const buildPrompt = useCallback(
    () => buildSubformSummaryPrompt({ workflowTemplate, orderedComponents, fields }),
    [workflowTemplate, orderedComponents, fields],
  );

  return (
    <AiSummary
      scope={{ type: 'card', id: entityId }}
      buildPrompt={buildPrompt}
      summary={summary}
      onSummaryGenerated={onSummaryGenerated}
      testIDPrefix="subform"
    />
  );
}
